import {
  Coffee,
  Trees,
  Palette,
  Users,
  TabletSmartphone,
  Flower2,
  Heart,
  MessagesSquare,
  Home,
  Smile,
} from "lucide-react";

export const IMAGES = {
  hero: "https://images.unsplash.com/photo-1706806594789-4cc731ded0e4?auto=format&fit=crop&w=900&h=1125&q=72",
  reading:
    "https://images.unsplash.com/photo-1758691031958-29541f3207a5?auto=format&fit=crop&w=640&h=800&q=68",
  gardenWalk:
    "https://images.unsplash.com/photo-1593068919175-24657e06ae91?auto=format&fit=crop&w=900&h=900&q=70",
  tea: "https://images.unsplash.com/photo-1701546859325-0c939f08734b?auto=format&fit=crop&w=800&h=800&q=70",
};

export const NAV_LINKS = [
  { label: "Home", target: "#home", testId: "nav-link-home" },
  { label: "About", target: "#about", testId: "nav-link-about" },
  { label: "Services", target: "#services", testId: "nav-link-services" },
  { label: "For Families", target: "#for-families", testId: "nav-link-for-families" },
  { label: "Become a Companion", target: "#become-companion", testId: "nav-link-become-companion" },
  { label: "FAQ", target: "#faq", testId: "nav-link-faq" },
  { label: "Contact", target: "#contact", testId: "nav-link-contact" },
];

export const MARQUEE_ITEMS = [
  "Dignified Conversation",
  "Gentle Outings & Strolls",
  "Warm Cups of Tea & Stories",
  "Favourite Hobbies & Board Games",
  "Reassurance for Loved Ones",
  "Non-Clinical, Heart-First Attention",
];

export const MANIFESTO_CHAPTERS = [
  {
    number: "01",
    pillar: "Compassion First",
    tagline: "Care without rushed clocks",
    description:
      "We are strictly non-medical by intention. Our companions never rush through a checklist; we focus entirely on presence, warmth, listening, and being truly there.",
    icon: Heart,
    tint: "mauve",
  },
  {
    number: "02",
    pillar: "Genuine Connection",
    tagline: "Matched on personality & passions",
    description:
      "Whether your loved one loves gardening, classical music, art history, or lively political debate, we pair them with companions who share kindred spirits.",
    icon: MessagesSquare,
    tint: "navy",
  },
  {
    number: "03",
    pillar: "Familiar Comfort",
    tagline: "In the safety of home and community",
    description:
      "Remaining independent within one's own treasured surroundings nurtures confidence, cognitive vitality, and emotional peace.",
    icon: Home,
    tint: "gold",
  },
  {
    number: "04",
    pillar: "Daily Joy",
    tagline: "Reigniting curiosity and laughter",
    description:
      "From baking ancestral scones to strolls through the park or sharing family scrapbooks, our visits bring light back to ordinary afternoons.",
    icon: Smile,
    tint: "sage",
  },
];

export const SERVICES = [
  {
    id: "social-companionship",
    title: "Social & Conversation Companionship",
    summary:
      "Stimulating conversation, reminiscing over photographs, reading aloud, sharing afternoon tea, and mutual laughter.",
    badge: "Popular",
    icon: Coffee,
    tint: "mauve",
    details: [
      "Tea & conversation sessions",
      "Brain-stimulating puzzles & chess",
      "Reading novels & poetry aloud",
      "Story capturing & memory sharing",
    ],
  },
  {
    id: "outings-errands",
    title: "Accompanied Outings & Cultural Visits",
    summary:
      "Gentle visits to local botanical gardens, galleries, farmers' markets, library trips, and theatre matinees.",
    badge: "Wellbeing",
    icon: Trees,
    tint: "sage",
    details: [
      "Door-to-door accompanied transit",
      "Garden centre & cafe visits",
      "Civic appointments & errands",
      "Safe arm-in-arm strolls",
    ],
  },
  {
    id: "hobbies-creativity",
    title: "Hobbies, Crafts & Cooking Together",
    summary:
      "Rekindling culinary recipes, tending windowsill herbs, watercolour painting, needlecraft, or listening to classic vinyl.",
    badge: "Enrichment",
    icon: Palette,
    tint: "gold",
    details: [
      "Baking favourite family treats",
      "Indoor & patio container gardening",
      "Knitting & painting sessions",
      "Music appreciation afternoons",
    ],
  },
  {
    id: "family-respite",
    title: "Family Respite & Peace of Mind",
    summary:
      "Reliable, compassionate support giving family caregivers guilt-free space to recharge while loved ones are delightfully engaged.",
    badge: "Reassurance",
    icon: Users,
    tint: "navy",
    details: [
      "Regular scheduled visiting slots",
      "Post-visit photo & note updates",
      "Reliable backup coverage",
      "Emergency point of contact",
    ],
  },
  {
    id: "digital-connection",
    title: "Digital Connection & Tech Assistance",
    summary:
      "Patient guidance with tablets, video calling grandchildren abroad, audiobooks, and keeping family chats vibrant.",
    badge: "Modern Care",
    icon: TabletSmartphone,
    tint: "mauve",
    details: [
      "FaceTime & WhatsApp setup with family",
      "Smart speaker & audiobook guidance",
      "Photo digitising & sharing",
      "Patient, repetitive tech tutorials",
    ],
  },
  {
    id: "wellness-support",
    title: "Gentle Movement & Wellbeing",
    summary:
      "Encouraging light stretching, seated yoga routines, fresh-air balcony breathing, and hydration reminders.",
    badge: "Vitality",
    icon: Flower2,
    tint: "sage",
    details: [
      "Gentle postural stretches",
      "Breathing in the garden",
      "Hydration & nutrition encouragement",
      "Positive daily rhythm support",
    ],
  },
];

export const HOW_IT_WORKS_STEPS = [
  {
    step: "01",
    title: "The Warm Introduction Call",
    desc: "A friendly 30-minute conversation with our Care Director to learn about your loved one's personality, history, routine, and interests.",
  },
  {
    step: "02",
    title: "The Personality Match",
    desc: "We select a companion whose background, hobbies, and temperament harmonise seamlessly with your family member.",
  },
  {
    step: "03",
    title: "A Gentle Cup of Tea Visit",
    desc: "A no-pressure introduction in the comfort of home to confirm the rapport feels effortless, respectful, and safe.",
  },
  {
    step: "04",
    title: "Consistent, Enriching Presence",
    desc: "Regular visits at your preferred rhythm, backed by warm family notes and dedicated check-ins after every visit.",
  },
];

export const FAMILY_COMMITMENTS = [
  {
    title: "Enhanced DBS & Thorough Vetting",
    text: "Every companion undergoes rigorous police checks, reference verification, and in-person interviews.",
  },
  {
    title: "Post-Visit Family Notes",
    text: "Receive warm, prompt summaries after each visit: what they chatted about, what made them smile, and gentle wellbeing observations.",
  },
  {
    title: "Consistency & Familiarity",
    text: "We never rotate strangers through your parent's front door. We build a single, cherished, consistent bond.",
  },
];

export const COMPANION_BENEFITS = [
  "Flexible, daytime hours that fit your lifestyle",
  "Above-market living wage compensation",
  "Deeply rewarding human connections",
  "Comprehensive ongoing support and community",
];

export const TESTIMONIALS = [
  {
    client: "Eleanor, 84 — with daughter Sarah",
    location: "Surrey",
    quote:
      "Mum was becoming increasingly withdrawn after Dad passed. Having Claire visit twice weekly has lit up her world again. They do crosswords and Mum now looks forward to Tuesday mornings like Christmas.",
    service: "Social & Conversation",
  },
  {
    client: "Robert, 79 — with son David",
    location: "Cotswolds",
    quote:
      "Living two hours away in London, I was constantly stressed that Dad was isolated. Adeya paired him with Julian, a retired geography tutor. They talk about maps and old trains for hours. Priceless peace of mind.",
    service: "Accompanied Outings",
  },
  {
    client: "Margaret, 88",
    location: "Edinburgh",
    quote:
      "Adeya isn't a nurse who checks boxes and rushes off. My companion Fiona is a true friend who makes me laugh, listens to my stories from the 1950s, and helps me keep my garden pots thriving.",
    service: "Hobbies & Crafts",
  },
];

export const FAQS = [
  {
    q: "What is the difference between non-medical companionship and home healthcare?",
    a: "Non-medical companionship focuses on emotional wellbeing, social interaction, cognitive stimulation, and accompaniment on outings. We do not administer medication or provide clinical or personal care. This allows our companions to offer 100% warm, unhurried, friendship-centred support.",
  },
  {
    q: "How do you vet and select Adeya companions?",
    a: "Every companion undergoes an enhanced DBS (Disclosure and Barring Service) background check, a minimum of two verified professional and personal references, and in-depth situational interviews to ensure empathy, patience, and reliability.",
  },
  {
    q: "Can we meet the companion before committing?",
    a: "Yes, absolutely. We always arrange an initial 'Cup of Tea' introduction visit. If either the client or family does not feel an immediate, natural connection, we happily introduce another companion at no charge.",
  },
  {
    q: "What are the minimum booking hours?",
    a: "To ensure meaningful, unhurried time together, our minimum visit duration is 2 hours. Most families book 2 to 4 visits per week, though daily companionship is also available.",
  },
  {
    q: "Can companions accompany my parent to appointments or shopping?",
    a: "Yes. Accompanied outings are among our most cherished services. Companions can travel with clients via taxi or public transport, and assist with shopping and social outings safely.",
  },
  {
    q: "How does Adeya keep family members informed?",
    a: "After every visit, your companion sends a brief, cheerful update by email or our private family portal, noting mood, activities enjoyed, and any gentle observations.",
  },
];

export const SERVICE_OPTIONS = [
  "Social & Conversation",
  "Accompanied Outings",
  "Hobbies & Cooking",
  "Family Respite",
  "Digital Tech Support",
  "Gentle Movement",
  "Undecided / General Chat",
];

export const CONTACT_INFO = {
  phone: "0800 920 4821",
  hours: "Mon–Fri, 8am–6pm",
  email: "care@adeyacompanions.co.uk",
  address: ["Adeya Companions Ltd.", "71-75 Shelton Street, Covent Garden", "London WC2H 9JQ, United Kingdom"],
};
